import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ loading: true, ok: null, data: null });

  useEffect(() => {
    if (!sessionId) { setStatus({ loading: false, ok: false, data: null }); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ loading: false, ok: true, data });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus({ loading: false, ok: false, data });
          return;
        }
      } catch (e) {
        // keep polling — webhook may still be delivering
      }
      tries += 1;
      if (tries < 15) { // ~30s total
        timer = setTimeout(poll, 2000);
      } else {
        setStatus({ loading: false, ok: null, data: null }); // unknown but assume paid → let user proceed
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (status.loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center">
          <Loader2 size={40} className="text-primary animate-spin mx-auto" />
          <div className="mt-4 font-serif-display text-2xl">Confirming your payment…</div>
          <div className="mt-2 text-sm text-muted-foreground">This usually takes just a few seconds.</div>
        </div>
      </main>
    );
  }

  const dollars = ((status.data?.amount || 0) / 100).toFixed(2);

  return (
    <main data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        {status.ok === true ? (
          <>
            <CheckCircle2 size={48} className="text-primary mx-auto" />
            <div className="mt-4 font-serif-display text-3xl">Payment received</div>
            {status.data?.amount > 0 && (
              <div className="mt-2 text-sm text-muted-foreground">
                We've received <strong>${dollars}</strong>. A receipt has been emailed to you.
              </div>
            )}
            <div className="mt-6 flex flex-col gap-2">
              <Button asChild className="rounded-full bg-primary text-primary-foreground">
                <Link to="/portal">Go to Parent Portal <ArrowRight size={14} className="ml-1.5" /></Link>
              </Button>
              <Button asChild variant="ghost" className="rounded-full">
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </>
        ) : status.ok === false ? (
          <>
            <XCircle size={48} className="text-destructive mx-auto" />
            <div className="mt-4 font-serif-display text-3xl">Payment not completed</div>
            <div className="mt-2 text-sm text-muted-foreground">Please try again or contact our concierge team.</div>
            <div className="mt-6"><Button asChild className="rounded-full bg-primary text-primary-foreground"><Link to="/portal">Return to portal</Link></Button></div>
          </>
        ) : (
          <>
            <CheckCircle2 size={48} className="text-primary mx-auto" />
            <div className="mt-4 font-serif-display text-3xl">Thank you</div>
            <div className="mt-2 text-sm text-muted-foreground">
              Your payment is processing. You'll receive an email receipt once it's confirmed.
            </div>
            <div className="mt-6"><Button asChild className="rounded-full bg-primary text-primary-foreground"><Link to="/portal">Return to portal</Link></Button></div>
          </>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
